import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { functions, db } from '../firebase/config';

// Styles
const styles = {
  container: {
    padding: '20px',
    maxWidth: '800px',
    margin: '0 auto',
  },
  header: {
    borderBottom: '1px solid #eee',
    paddingBottom: '10px',
    marginBottom: '20px',
  },
  card: {
    border: '1px solid #eee',
    borderRadius: '5px',
    padding: '15px',
    marginBottom: '15px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
  },
  statusBadge: {
    fontSize: '12px',
    padding: '3px 8px',
    borderRadius: '12px',
    fontWeight: 'normal',
    color: 'white',
  },
  button: {
    padding: '8px 15px',
    borderRadius: '4px',
    border: 'none',
    backgroundColor: '#007bff',
    color: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    marginRight: '10px',
  },
  printJobCard: {
    border: '1px solid #f0f0f0',
    borderRadius: '4px',
    padding: '10px',
    marginBottom: '10px',
    backgroundColor: '#f9f9f9',
  },
  printerSelect: {
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    width: '100%',
    marginBottom: '15px',
  },
  notification: {
    padding: '15px',
    backgroundColor: '#d4edda',
    borderRadius: '4px',
    marginBottom: '15px',
    color: '#155724',
  },
  error: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
  },
};

/**
 * Print Agent Manager Component
 * 
 * This component provides an interface for:
 * - Viewing all registered print agents
 * - Sending print jobs
 * - Monitoring print job status
 */
function PrintAgentManager() {
  const [agents, setAgents] = useState([]);
  const [printJobs, setPrintJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedPrinter, setSelectedPrinter] = useState('');

  // Load print agents on component mount
  useEffect(() => {
    fetchPrintAgents();
  }, []);

  // Fetch print agents from Firestore
  const fetchPrintAgents = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Using Cloud Function to get agents with proper filtering
      const listPrintAgents = httpsCallable(functions, 'listPrintAgents');
      const result = await listPrintAgents();
      
      if (result.data.success) {
        setAgents(result.data.agents);
        
        // Set the first online agent as selected by default
        const onlineAgent = result.data.agents.find(agent => agent.status === 'ONLINE');
        if (onlineAgent) {
          setSelectedAgent(onlineAgent);
          if (onlineAgent.printers && onlineAgent.printers.length > 0) {
            setSelectedPrinter(onlineAgent.printers[0].name);
          }
        }
      } else {
        setError(result.data.error || 'Failed to fetch print agents');
      }
    } catch (err) {
      setError('Error loading print agents: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch print jobs
  const fetchPrintJobs = async () => {
    if (!selectedAgent) return;
    
    try {
      // Query Firestore directly for jobs associated with this agent
      const jobsQuery = query(
        collection(db, 'printJobs'),
        where('agentId', '==', selectedAgent.id),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(jobsQuery);
      const jobs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || null,
        updatedAt: doc.data().updatedAt?.toDate() || null,
      }));
      
      setPrintJobs(jobs);
    } catch (err) {
      setError('Error loading print jobs: ' + err.message);
    }
  };

  // When selected agent changes, fetch its print jobs
  useEffect(() => {
    if (selectedAgent) {
      fetchPrintJobs();
    } else {
      setPrintJobs([]);
    }
  }, [selectedAgent]);

  // Send a print job
  const sendPrintJob = async (content) => {
    if (!selectedAgent || !selectedPrinter) {
      setError('Please select an agent and printer');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Call queuePrintJob function
      const queuePrintJob = httpsCallable(functions, 'queuePrintJob');
      const result = await queuePrintJob({
        agentId: selectedAgent.id,
        printer: selectedPrinter,
        printData: content,
        options: {
          copies: 1,
        },
        // Add any metadata you want to track
        documentId: 'test-document',
        userId: 'current-user',
      });
      
      if (result.data.success) {
        setNotification(`Print job sent! Job ID: ${result.data.jobId}`);
        // Refresh jobs list
        fetchPrintJobs();
      } else {
        setError(result.data.error || 'Failed to send print job');
      }
    } catch (err) {
      setError('Error sending print job: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Send a test print
  const sendTestPrint = () => {
    const testContent = `
FLL Marina Print Test
===================
Date: ${new Date().toLocaleString()}
Agent: ${selectedAgent?.name || 'Unknown'}
Printer: ${selectedPrinter}

This is a test print from the Print Agent Manager.
If you can read this, printing is working correctly!
`;
    
    sendPrintJob(testContent);
  };

  // Send a command to the agent
  const sendCommand = async (type, data = {}) => {
    if (!selectedAgent) {
      setError('Please select an agent');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Call sendAgentCommand function
      const sendAgentCommand = httpsCallable(functions, 'sendAgentCommand');
      const result = await sendAgentCommand({
        agentId: selectedAgent.id,
        type,
        data
      });
      
      if (result.data.success) {
        setNotification(`Command ${type} sent! Command ID: ${result.data.commandId}`);
      } else {
        setError(result.data.error || 'Failed to send command');
      }
    } catch (err) {
      setError('Error sending command: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Refresh the printers list
  const refreshPrinters = () => {
    sendCommand('REFRESH_PRINTERS');
    // Refresh agents after a delay to see the updated printer list
    setTimeout(() => {
      fetchPrintAgents();
    }, 5000);
  };

  // Render agent status badge
  const renderStatusBadge = (status) => {
    let color = '#6c757d'; // Default gray
    
    if (status === 'ONLINE') {
      color = '#28a745'; // Green
    } else if (status === 'OFFLINE') {
      color = '#dc3545'; // Red
    } else if (status === 'CONNECTING') {
      color = '#ffc107'; // Yellow
    }
    
    return (
      <span style={{ ...styles.statusBadge, backgroundColor: color }}>
        {status}
      </span>
    );
  };

  // Render job status badge
  const renderJobStatusBadge = (status) => {
    let color = '#6c757d'; // Default gray
    
    if (status === 'COMPLETED') {
      color = '#28a745'; // Green
    } else if (status === 'FAILED') {
      color = '#dc3545'; // Red
    } else if (status === 'PROCESSING') {
      color = '#17a2b8'; // Blue
    } else if (status === 'PENDING') {
      color = '#ffc107'; // Yellow
    }
    
    return (
      <span style={{ ...styles.statusBadge, backgroundColor: color }}>
        {status}
      </span>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2>Print Agent Manager</h2>
        <button 
          style={styles.button}
          onClick={fetchPrintAgents}
          disabled={loading}
        >
          Refresh Agents
        </button>
      </div>
      
      {/* Notification area */}
      {notification && (
        <div style={styles.notification}>
          {notification}
          <button 
            style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}
            onClick={() => setNotification(null)}
          >
            ✕
          </button>
        </div>
      )}
      
      {/* Error display */}
      {error && (
        <div style={{ ...styles.notification, ...styles.error }}>
          {error}
          <button 
            style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}
            onClick={() => setError(null)}
          >
            ✕
          </button>
        </div>
      )}
      
      {/* Agent selection */}
      <div style={styles.card}>
        <h3>Select Print Agent</h3>
        {loading ? (
          <p>Loading agents...</p>
        ) : agents.length === 0 ? (
          <p>No print agents found.</p>
        ) : (
          <div>
            {agents.map(agent => (
              <div
                key={agent.id}
                style={{
                  ...styles.card,
                  border: selectedAgent?.id === agent.id ? '2px solid #007bff' : undefined,
                  cursor: 'pointer'
                }}
                onClick={() => setSelectedAgent(agent)}
              >
                <div style={styles.cardHeader}>
                  <h4>{agent.name}</h4>
                  {renderStatusBadge(agent.status)}
                </div>
                <p><strong>ID:</strong> {agent.id}</p>
                <p><strong>Hostname:</strong> {agent.hostname}</p>
                <p><strong>Last Seen:</strong> {agent.lastSeen ? new Date(agent.lastSeen._seconds * 1000).toLocaleString() : 'Never'}</p>
                <p><strong>Connection Mode:</strong> {agent.connectionMode || 'HTTP'}</p>
                <p><strong>Printers:</strong> {agent.printers?.length || 0}</p>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Printer selection and print controls */}
      {selectedAgent && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3>Print Controls</h3>
            <button 
              style={styles.button}
              onClick={refreshPrinters}
              disabled={loading || selectedAgent.status !== 'ONLINE'}
            >
              Refresh Printers
            </button>
          </div>
          
          {selectedAgent.status !== 'ONLINE' ? (
            <p>Agent is offline. Cannot send print jobs.</p>
          ) : selectedAgent.printers?.length === 0 ? (
            <p>No printers found for this agent.</p>
          ) : (
            <div>
              <label>
                <div style={{ marginBottom: '5px' }}>Select Printer:</div>
                <select 
                  style={styles.printerSelect}
                  value={selectedPrinter}
                  onChange={(e) => setSelectedPrinter(e.target.value)}
                >
                  <option value="">-- Select a printer --</option>
                  {selectedAgent.printers.map(printer => (
                    <option key={printer.name} value={printer.name}>
                      {printer.name} ({printer.status})
                    </option>
                  ))}
                </select>
              </label>
              
              <button 
                style={styles.button}
                onClick={sendTestPrint}
                disabled={loading || !selectedPrinter}
              >
                Send Test Print
              </button>
              
              <button 
                style={styles.button}
                onClick={() => sendCommand('TEST_PRINT', { printer: selectedPrinter })}
                disabled={loading || !selectedPrinter}
              >
                Remote Test Print
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Print jobs list */}
      {selectedAgent && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3>Print Jobs</h3>
            <button 
              style={styles.button}
              onClick={fetchPrintJobs}
              disabled={loading}
            >
              Refresh Jobs
            </button>
          </div>
          
          {printJobs.length === 0 ? (
            <p>No print jobs found for this agent.</p>
          ) : (
            <div>
              {printJobs.map(job => (
                <div key={job.id} style={styles.printJobCard}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <strong>Job ID: {job.id.substring(0, 8)}...</strong>
                    {renderJobStatusBadge(job.status)}
                  </div>
                  <p><strong>Printer:</strong> {job.printer || 'Not specified'}</p>
                  <p><strong>Created:</strong> {job.createdAt?.toLocaleString() || 'Unknown'}</p>
                  <p><strong>Updated:</strong> {job.updatedAt?.toLocaleString() || 'Unknown'}</p>
                  {job.errorMessage && (
                    <p style={{ color: '#dc3545' }}><strong>Error:</strong> {job.errorMessage}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PrintAgentManager; 